import Course from '@/content/models/course.model';
import { IMemberPosition } from '@/core/interfaces/member-position.interface';
import { _APP_STORE } from '@/core/values/app.values';
import { COURSES_EVENTS } from '@/courses/values/courses-events.values';
import { COURSE_STORAGE_KEYS, COURSE_TABS } from '@/courses/values/courses.values';
import { HOMEWORKS_EVENTS, _HOMEWORKS_STORE } from '@/homework/values/homeworks.values';
import { _CLASSES_STORE } from '@/classes/values/classes.values';
import { Status } from '@/models/status.model';
import { ScrollProvider } from '@/providers/scroll.provider';
import { LocalStorageService } from '@/services/local-storage.service';
import { ScreenMirroringMixin } from '@/screen-mirroring/mixins/screen-mirroring.mixin';
import { SCREEN_MIRRORING_EVENTS } from '@/values/screen.mirroring.values';
import Component from 'vue-class-component';
import { Mixins, Prop, Watch } from 'vue-property-decorator';
import KEYS from '@/values/keys';
import { CourseToolsMixin } from '@/courses/mixins/course-tools.mixin';
import { MY_CONTENT_ROUTES } from '@/app/private/my-content/my-content.router';
import { SectionsProvider } from '@/content/providers/sections.provider';

@Component({
  name: 'ed-class-header'
})
export default class EdClassHeaderComponent extends Mixins(CourseToolsMixin, ScreenMirroringMixin) {

  @Prop() readonly beforeItems: any[];
  @Prop() readonly classId: number;

  /* Outputs */
  public onStatus: string = 'onStatus';
  public onCourseSelected: string = 'onCourseSelected';

  /* Data */
  public tabSelected: number = null;
  public status: Status = new Status();
  public coursesTabsId: string = 'coursesTabs';
  public canGoNext: boolean = true;
  public canGoPrev: boolean = true;
  public isArrowShown: boolean = true;
  public isTabsShown: boolean = false;
  public selectedClassId: number;

  get institutionsLoaded() {
    return this.$store.state.AppStore.institutionsLoaded;
  }

  get courses(): Course[] {
    const courses = this.$store.state.CourseStore.courses.content || [];
    return courses.filter(c => c.classId === this.selectedClassId);
  }

  get classesIds() {
    return new Set(this.courses.map(el => el.classId))
  }

  get tabs() {
    return this.courses.map(c => {
      return {
        id: c.id,
        title: c.title,
        classId: c.classId,
        members: this.getMembersConnected(c.id, c.classId),
        tab: COURSE_TABS.COURSE_TAB
      }
    });
  }

  get membersPosition() {
    return this.$store.state.AppStore.membersPosition;
  }

  get socketConnected(): boolean {
    return this.$store.state.AppStore.socketConnected;
  }

  get isStudent() {
    return this.$store.state.AuthStore.role == KEYS.ROLES.BUYER || (this.$store.getters['AuthStore/isSubstituteTeacher'] && !this.$store.state.ClassesStore.substituteTeacherCanEditClass.get(this.classId));
  }

  get meetingsClasses() {
    return this.$store.state.MeetingsStore.meetingsClasses && 
      this.$store.state.MeetingsStore.meetingsClasses.map(el => el.classId);
  }

  get ulStyle() {
    let style: any = {};
    if(!this.isArrowShown) {
      style = {
        overflowX: 'hidden'
      };
    }
    return style; 
  }

  getMembersConnected(courseId: number, classId: number): IMemberPosition[] {
    let members = [];
    if(this.isStudent) {
      members = this.membersPosition.filter(m => {
        return (m.courseId == courseId && m.classId == classId);
      });
    }
    return members;
  }

  setPosition(course: Course) {
    if(!this.isStudent) {
      this.setTeacherPosition({
        courseId: course.id,
        classId: course.classId 
      });
    }
  }

  clearPosition() {
    if(!this.isStudent) {
      this.$store.dispatch(_APP_STORE.MUTATIONS.CLEAN_TEACHER_POSITION);
      this.setTeacherPosition({ courseId: 0, classId: 0 });
    }
  }

  onChangeTab(courseId?: number, classId?: number, emitMirroringEvent: boolean = true) {
    if(courseId && classId) {
      let course = this.courses.find(c => c.id == courseId && c.classId == classId);
      this.tabSelected = this.courses.indexOf(course);
      this.setPosition(course);
      let tabElement = this.$refs[`COURSE_TAB_${courseId}_${classId}`];
      if(tabElement) {
        tabElement[0].scrollIntoView();
      }
      if(emitMirroringEvent) {
        this.$edEventBus.$emit(
          SCREEN_MIRRORING_EVENTS.CLICK,
          this,
          {
            target: SCREEN_MIRRORING_EVENTS.ON_OPEN_COURSE_TAB,
            courseId: courseId,
            classId: classId
          }
        );
      }
      this.$emit(this.onCourseSelected, courseId);
      this.resetUserNavigation(courseId, classId);
      let courseLocalStore = {...course, sections: [], 
        deckLibrary: {},
        materials: {},
        projects: {},
        materialsList: {} };
      LocalStorageService.setItem(COURSE_TABS.COURSE_OBJ, courseLocalStore);
      LocalStorageService.setItem(COURSE_TABS.COURSE_ID, this.tabSelected);
    }
  }

  getCoursesFromClassId(classId): Promise<void> {
    return new Promise((resolve, reject) => {
      this.$Courses.Store.getCoursesA({
        classId
      }
      ).then(() => {
        resolve();
      })
      .catch(() => {
        reject();
      });
    });
  }

  checkLocalStorage() {
    let course = this.getCourseFromLocalStorage();
    let courseId = null;
    if(course) {
      courseId = course.id
    } else if(this.courses.length > 0) {
      courseId = this.courses[0].id;
    }
    if(courseId) {
      this.onChangeTab(
        courseId,
        this.selectedClassId,
        true
      );
    }
  }

  getCourseFromLocalStorage(): Course {
    let courseSaved: Course = LocalStorageService.getItem(COURSE_TABS.COURSE_OBJ);
    let i = LocalStorageService.getItem(COURSE_TABS.COURSE_ID);
    let course = null;
    if(courseSaved && this.tabs[i]) {
      if(this.tabs[i].id == courseSaved.id && this.tabs[i].classId == courseSaved.classId) {
        course = this.tabs[i];
      }
    }
    return course;
  }

  async setCourses() {
    try {
      this.status.setLoading();
      await this.getCoursesFromClassId(this.selectedClassId);
      await this.$store.dispatch(_CLASSES_STORE.ACTIONS.CAN_SUBSTITUTE_TEACHER_EDIT_CLASS_A, {classId: this.selectedClassId});
      
      if(!this.institutionsLoaded || !this.socketConnected) {
        // this.$store.subscribe((mutation) => {
        //   if(mutation.type == APP_STORE.MUTATIONS.SET_INSTITUTIONS_LOADED) {
        //     if(this.socketConnected) this.setElements();
        //   }
        //   if(mutation.type == _APP_STORE.MUTATIONS.SET_SOCKET_CONNECTED) {
        //     if(this.institutionsLoaded) this.setElements();
        //   }
        // });
      } else {
        this.setElements();
      }
    } catch(err) {
      this.status.setError();
    }
  }

  setElements() {
    this.checkLocalStorage();
    this.status.setLoad();
    this.setScrollSize();
    this.setInitScroll();
    this.isTabsShown = true;
  }

  setInitScroll() {
    setTimeout(() => {
      let container = document.querySelector(`#${this.coursesTabsId}`);
      if(this.tabSelected) {
        let tabs: any = document.querySelectorAll(`#${this.coursesTabsId} li`);
        let left = (tabs[this.tabSelected].offsetLeft) - 63;
        ScrollProvider.scrollX(container, left);
      }
    }, 100);
  }

  setScrollSize() {
    setTimeout(() => {
      let container = document.querySelector(`#${this.coursesTabsId}`);
      this.isArrowShown = container.scrollWidth > container.clientWidth;
    }, 100);
  }

  setControlsScroll() {
    let tabs = document.querySelector(`#${this.coursesTabsId}`);
    this.canGoPrev = tabs.scrollLeft > 0;
    this.canGoNext = tabs.scrollLeft < (tabs.scrollWidth - tabs.clientWidth);
  }

  onScroll() {
    this.setControlsScroll();
  }

  goToPrev() {
    let tabs = document.querySelector(`#${this.coursesTabsId}`);
    let left = 0;
    if(tabs.scrollLeft >= tabs.clientWidth) {
      left = tabs.scrollLeft - tabs.clientWidth;
    }
    ScrollProvider.scrollX(tabs, left);
  }

  goToNext() {
    let tabs = document.querySelector(`#${this.coursesTabsId}`);
    let left = tabs.clientWidth + tabs.scrollLeft;
    ScrollProvider.scrollX(tabs, left);
  }

  listenMembersPosition(value: IMemberPosition) {
    this.$App.Store.setMemberPosition(value)
  }

  processClickServerEvent({courseId, classId}: { courseId:string, classId:string }) {
    this.onChangeTab(
      parseInt(courseId),
      parseInt(classId),
      false
    );
  }

  async onChangeTabWithAutoScroll(message:{currentCourseId?:number, currentClassId?:number, currentSectionId?:number, currentMaterialId?:number, currentProjectId?:number}) {
    
    if( !message.currentCourseId || !message.currentClassId ) {
      return;
    }

    let currentSectionState = LocalStorageService.getItem(COURSE_STORAGE_KEYS.ED_COURSE_VIEWER_SECTION_STATE) || {};
    let currentMaterialState = LocalStorageService.getItem(COURSE_STORAGE_KEYS.ED_COURSE_VIEWER_MATERIAL_STATE) || {};
    let currentProjectState = LocalStorageService.getItem(COURSE_STORAGE_KEYS.ED_COURSE_VIEWER_PROJECT_STATE) || {};
    
    delete currentSectionState[message.currentCourseId];
    delete currentProjectState[message.currentCourseId];
    delete currentMaterialState[message.currentCourseId];

    if(message.currentSectionId) {
      currentSectionState = {...currentSectionState, [message.currentCourseId]:message.currentSectionId};
    }

    if(message.currentMaterialId) {
      currentMaterialState = {...currentMaterialState, [message.currentCourseId]:message.currentMaterialId};
    }

    if(message.currentProjectId) {
      currentProjectState = {...currentProjectState, [message.currentCourseId]:message.currentProjectId};
    }

    LocalStorageService.setItem(COURSE_STORAGE_KEYS.ED_COURSE_VIEWER_SECTION_STATE, currentSectionState);
    LocalStorageService.setItem(COURSE_STORAGE_KEYS.ED_COURSE_VIEWER_MATERIAL_STATE, currentMaterialState);
    LocalStorageService.setItem(COURSE_STORAGE_KEYS.ED_COURSE_VIEWER_PROJECT_STATE, currentProjectState);

    if( this.selectedClassId != message.currentClassId ) {
      await this.$router.push(MY_CONTENT_ROUTES.CLASSES.PATH.replace(':id', message.currentClassId.toString()));
      this.selectedClassId = message.currentClassId;
      this.getCoursesFromClassId(this.selectedClassId).then(() => {
        this.$edEventBus.$emit(SCREEN_MIRRORING_EVENTS.ON_OPEN_COURSE_TAB, {courseId:message.currentCourseId, classId:message.currentClassId});
      });

    } else {
      this.$edEventBus.$emit(SCREEN_MIRRORING_EVENTS.ON_OPEN_COURSE_TAB, {courseId:message.currentCourseId, classId:message.currentClassId});
      
      try{
        if(message.currentSectionId) {

          //clean EdObjectPreview's content
          this.goToComponentSelected({ 
            courseId: message.currentCourseId,
            classId: message.currentClassId, 
            componentInfo: { 
              name: null,
              section: null,
              edition: false 
            }
          });

          this.$nextTick(() => {
            //let EdObjectPreview displays section object
            this.goToSectionPreview({courseId:message.currentCourseId, sectionId: message.currentSectionId, classId:message.currentClassId});
          });

        }
        
        if(message.currentMaterialId) {
          this.goToMaterialPreview({courseId:message.currentCourseId, materialId: message.currentMaterialId});
        }
  
        if(message.currentProjectId) {
          this.goToProjectPreview({courseId:message.currentCourseId, projectId: message.currentProjectId});
        }
      } catch(ex) {
        //do nothing
      }
    }

    //update course information in case any update during device disconnection
    this.$edEventBus.$emit(SCREEN_MIRRORING_EVENTS.ON_REFRESH_COURSE, {courseId:message.currentCourseId, sectionId:message.currentSectionId});
    this.$edEventBus.$emit(HOMEWORKS_EVENTS.WHITEBOARD_CLOSED_ON_DELETE, {courseId: message.currentCourseId});
  }

  resetUserNavigation(courseId:number, classId:number) {
    if( this.isScreenMirroringActive ) {

      //close all folders
      this.$edEventBus.$emit(COURSES_EVENTS.KEEP_OPEN_FOLDERS, {});

    }
  }

  async refreshSection({ courseId , classId, sectionId}) {
    let parentId = null;
    let course = this.courses.find(c => c.id === courseId);
    if(course){
      let section = SectionsProvider.getSectionByID(course.sections, sectionId);
      parentId = section?.parentId;
    }
    await this.goToRefreshSectionsTree({ courseId , classId, sectionId, parentId });
  }

  mounted() {
    this.selectedClassId = this.classId;
    window.addEventListener('resize', this.setScrollSize);
    this.setCourses();
    this.$edEventBus.$on('listenMembersPosition', this.listenMembersPosition);
    this.$edEventBus.$on( SCREEN_MIRRORING_EVENTS.ON_OPEN_COURSE_TAB, this.processClickServerEvent);
    this.$edEventBus.$on( SCREEN_MIRRORING_EVENTS.SYNCHRONIZE_SCREENS, this.onChangeTabWithAutoScroll);
    this.$edEventBus.$on( SCREEN_MIRRORING_EVENTS.RESET_USER_NAVIGATION, this.resetUserNavigation);
    this.$edEventBus.$on( COURSES_EVENTS.REFRESH_ON_SECTION_UPDATED, this.refreshSection);
  }

  beforeDestroy() {
    this.$edEventBus.$off('listenMembersPosition');
    this.$edEventBus.$off( SCREEN_MIRRORING_EVENTS.ON_OPEN_COURSE_TAB );
    this.$edEventBus.$off( SCREEN_MIRRORING_EVENTS.SYNCHRONIZE_SCREENS );
    this.$edEventBus.$off( SCREEN_MIRRORING_EVENTS.RESET_USER_NAVIGATION);
    this.$edEventBus.$off( COURSES_EVENTS.REFRESH_ON_SECTION_UPDATED);
  }

  destroyed() {
    window.removeEventListener('resize', this.setScrollSize);
  }

  @Watch('meetingsClasses')
  registerClassesSocket() {
    if(this.classesIds && this.meetingsClasses) {
      let classesToListen = Array.from(this.classesIds)
        .filter(el => !this.meetingsClasses.includes(el));
      classesToListen.forEach(classId => {
        this.$edEventBus.$emit('createClassListener', classId)
      })
    }
  }

}